import { ImagePlus, X } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type ChangeEvent } from 'react';
import { MAX_TICKET_IMAGES } from '@sf/shared';
import { t } from '../lib/copy';
import { isAllowedImageType } from '../lib/imageCompress';

interface ImageUploaderProps {
  files: File[];
  onChange: (files: File[]) => void;
  /** 0..1 per file while uploading */
  progress?: number[] | null;
  disabled?: boolean;
}

export function ImageUploader({ files, onChange, progress, disabled }: ImageUploaderProps) {
  const id = useId();
  const [message, setMessage] = useState<string | null>(null);
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const onSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    const allowed = picked.filter((f) => isAllowedImageType(f.type));
    let note: string | null = allowed.length < picked.length ? t('ticket.upload.badType') : null;
    const room = MAX_TICKET_IMAGES - files.length;
    if (allowed.length > room) note = t('ticket.upload.tooMany');
    setMessage(note);
    if (allowed.length) onChange([...files, ...allowed.slice(0, Math.max(0, room))]);
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-ink" id={`${id}-label`}>
        Photos <span className="text-slate">(optional, up to {MAX_TICKET_IMAGES})</span>
      </span>
      <ul className="grid grid-cols-3 gap-3 sm:grid-cols-5" aria-labelledby={`${id}-label`}>
        {files.map((file, i) => (
          <li
            key={`${file.name}-${i}`}
            className="relative aspect-square overflow-hidden rounded-input border border-stone bg-mist"
          >
            <img
              src={previews[i]}
              alt={`Attachment ${i + 1}: ${file.name}`}
              className="size-full object-cover"
            />
            {progress ? (
              <div className="absolute inset-x-0 bottom-0 bg-ink/60 p-1.5">
                <div
                  role="progressbar"
                  aria-label={`Upload progress for photo ${i + 1}`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round((progress[i] ?? 0) * 100)}
                  className="h-1.5 overflow-hidden rounded-full bg-white/30"
                >
                  <div
                    className="h-full bg-white transition-[width]"
                    style={{ width: `${(progress[i] ?? 0) * 100}%` }}
                  />
                </div>
              </div>
            ) : (
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                className="absolute right-1 top-1 inline-flex size-8 items-center justify-center rounded-full bg-ink/70 text-white hover:bg-ink"
                aria-label={`Remove photo ${i + 1}`}
              >
                <X className="size-4" aria-hidden />
              </button>
            )}
          </li>
        ))}
        {files.length < MAX_TICKET_IMAGES && !progress && (
          <li>
            <label
              htmlFor={id}
              className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-input border-2 border-dashed border-stone text-slate hover:border-brand-light hover:text-brand focus-within:outline-2 focus-within:outline-brand-light"
            >
              <ImagePlus className="size-6" aria-hidden />
              <span className="text-xs font-medium">Add photo</span>
              <input
                id={id}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                disabled={disabled}
                onChange={onSelect}
                className="sr-only"
              />
            </label>
          </li>
        )}
      </ul>
      <p className="text-xs text-slate">
        JPG, PNG or WebP. Large photos are resized automatically.
      </p>
      {message && (
        <p role="alert" className="text-sm text-danger-700">
          {message}
        </p>
      )}
    </div>
  );
}
